import type { UseFormReturn } from 'react-hook-form';
import { getLabelFromId } from '@core/utils/collection';
import { idToForm, optionalNumber, optionalTrimmed } from '@core/utils/form';
import type { Color } from '../../api/color';
import type {
  DesignPreset,
  UpdateDesignPresetData,
} from '../../api/designPreset';
import type { Fabric } from '../../api/fabric';
import type { LegacyCollar } from '../../api/legacyCollar';
import type { MaterialColor } from '../../api/materialColor';
import type { PastCombination } from '../../api/pastCombination';
import type { StoleCut } from '../../api/stoleCut';
import type { Vestment } from '../../api/vestment';
import type { VestmentType } from '../enum/vestmentType';
import {
  type ChasubleApiValues,
  type ChasubleFeatures,
  type ChasublePanelField,
  type ChasubleValueFormValues,
  chasubleValuesFromApi,
  getChasubleFeatures,
  getChasubleMaterialField,
  getChasublePanelFields,
  getDesignPresetFeatures,
  getGothicTypeLabel,
  getPatchingTypeLabel,
} from './chasuble';
import {
  type CollarOption,
  findCollarByName,
  getCollarOptions,
} from './collar';
import {
  fabricSegment,
  getMaterialSummary,
  segment,
  segments,
  withSegmentColor,
} from './orderItem/helpers';
import type {
  ChasubleItemFormValues,
  OrderItemSummarySegment,
  StoleItemFormValues,
} from './orderItem/types';
import {
  type StoleFeatures,
  getStoleDesignPresetFeatures,
  getStoleFeatures,
} from './stole';

export type PresetFeatures =
  | { kind: 'chasuble'; features: ChasubleFeatures }
  | { kind: 'stole'; features: StoleFeatures };

/** Chasuble preset features, or stole preset features for a newer stole design. */
export function getPresetFeatures(
  designType: VestmentType | null | undefined
): PresetFeatures | undefined {
  const chasuble = getDesignPresetFeatures(designType);
  if (chasuble) {
    return { kind: 'chasuble', features: chasuble };
  }
  const stole = getStoleDesignPresetFeatures(designType);
  return stole ? { kind: 'stole', features: stole } : undefined;
}

export type DesignPresetFormValues = ChasubleValueFormValues & {
  name: string;
  stoleCut: string;
  withCord: boolean;
  cord: string;
  withTassel: boolean;
  tassel: string;
  withPatching: boolean;
  patchingFabric: string;
  patchingColor: string;
};

type PresetApiValues = ChasubleApiValues & {
  name?: string;
  stoleCut?: { id: number } | null;
  cord?: { id: number } | null;
  tassel?: { id: number } | null;
  patchingFabric?: { id: number } | null;
  patchingColor?: { id: number } | null;
};

export function designPresetToFormValues(
  values?: PresetApiValues | null
): DesignPresetFormValues {
  const cord = idToForm(values?.cord);
  const tassel = idToForm(values?.tassel);
  const patchingFabric = idToForm(values?.patchingFabric);
  const patchingColor = idToForm(values?.patchingColor);
  return {
    ...chasubleValuesFromApi(values ?? {}),
    name: values?.name ?? '',
    stoleCut: idToForm(values?.stoleCut),
    withCord: cord !== '',
    cord,
    withTassel: tassel !== '',
    tassel,
    withPatching: patchingFabric !== '' || patchingColor !== '',
    patchingFabric,
    patchingColor,
  };
}

/** Only sends the fields the features have, since the backend rejects others. */
export function designPresetFormToPayload(
  values: DesignPresetFormValues,
  features: PresetFeatures
): UpdateDesignPresetData {
  if (features.kind === 'stole') {
    const stole = features.features;
    return {
      name: values.name.trim(),
      panelFabric: optionalNumber(values.panelFabric),
      panelColor: optionalNumber(values.panelColor),
      ...(stole.hasCuts && { stoleCut: optionalNumber(values.stoleCut) }),
      ...(values.withCord && { cord: optionalNumber(values.cord) }),
      ...(values.withTassel && { tassel: optionalNumber(values.tassel) }),
      ...(stole.patching &&
        values.withPatching && {
          patchingFabric: optionalNumber(values.patchingFabric),
          patchingColor: optionalNumber(values.patchingColor),
        }),
    };
  }
  return chasublePresetPayload(values, features.features);
}

function chasublePresetPayload(
  values: DesignPresetFormValues,
  features: ChasubleFeatures
): UpdateDesignPresetData {
  return {
    name: values.name.trim(),
    bodyFabric: optionalNumber(values.bodyFabric),
    bodyColor: optionalNumber(values.bodyColor),
    ...(features.panel && {
      panelFabric: optionalNumber(values.panelFabric),
      panelColor: optionalNumber(values.panelColor),
    }),
    ...(features.patching && {
      patchingType: optionalTrimmed(values.patchingType),
      thread: optionalNumber(values.thread),
    }),
    ...(features.gothic && {
      gothicType: optionalTrimmed(values.gothicType),
      galoon: optionalNumber(values.galoon),
      panelDimension: optionalTrimmed(values.panelDimension),
    }),
    ...((features.patching || features.gothic) && {
      trimming: optionalNumber(values.trimming),
    }),
    ...(features.roman && {
      liningFabric: optionalNumber(values.liningFabric),
      liningColor: optionalNumber(values.liningColor),
      stoleSize: optionalTrimmed(values.stoleSize),
    }),
    ...(features.collar && {
      collar: optionalNumber(values.collar),
      collarFabric: optionalNumber(values.collarFabric),
      collarColor: optionalNumber(values.collarColor),
    }),
  };
}

/** The vestment whose collars a preset of a design of this type picks from. */
export function getDesignPresetCollarVestment(
  vestments: Vestment[],
  designType: VestmentType | null | undefined
): Vestment | undefined {
  const matches = vestments.filter((vestment) => vestment.type === designType);
  return matches.find((vestment) => !vestment.isHidden) ?? matches[0];
}

const PANEL_FIELD_TO_VALUE_KEYS: Partial<
  Record<ChasublePanelField, (keyof ChasubleValueFormValues)[]>
> = {
  fabric: ['panelFabric'],
  color: ['panelColor'],
  gothicType: ['gothicType'],
  galoon: ['galoon'],
  dimension: ['panelDimension'],
  trimming: ['trimming'],
  liningFabric: ['liningFabric'],
  liningColor: ['liningColor'],
  stoleSize: ['stoleSize'],
};

/** Form keys the current vestment type accepts from a preset. */
function getApplicableChasubleValueKeys(
  features: ChasubleFeatures,
  values: ChasubleValueFormValues
): (keyof ChasubleValueFormValues)[] {
  const keys: (keyof ChasubleValueFormValues)[] = ['bodyFabric', 'bodyColor'];
  if (features.panel) {
    const panelFields = getChasublePanelFields(features, values);
    for (const field of panelFields) {
      if (field === 'patching') {
        keys.push('patchingType');
        const material = getChasubleMaterialField(features, values);
        if (material === 'thread' || material === 'trimming') {
          keys.push(material);
        }
        continue;
      }
      const mapped = PANEL_FIELD_TO_VALUE_KEYS[field];
      if (mapped) {
        keys.push(...mapped);
      }
    }
  }
  if (features.collar) {
    keys.push('collar', 'collarFabric', 'collarColor');
  }
  return keys;
}

/**
 * Copies non-empty chasuble values the item's type has onto the form.
 * Collar is matched by name (design vestment collars, then legacy collars).
 */
export function applyChasubleDesignPreset(
  methods: UseFormReturn<ChasubleItemFormValues>,
  values: ChasubleApiValues,
  vestment: Vestment | undefined,
  vestments: Vestment[],
  collars: LegacyCollar[]
): void {
  const features = getChasubleFeatures(vestment?.type);
  if (!features) {
    return;
  }
  const formValues = chasubleValuesFromApi(values);
  const designType = methods.getValues('design')?.vestmentType;
  const sourceVestment = getDesignPresetCollarVestment(vestments, designType);
  const targetCollars = getCollarOptions(vestment, collars);
  const sourceCollars: CollarOption[] = [
    ...(sourceVestment?.collars ?? []),
    ...collars,
  ];

  for (const key of getApplicableChasubleValueKeys(features, formValues)) {
    const value = formValues[key];
    if (!value) {
      continue;
    }
    if (key === 'collar') {
      const name = getLabelFromId(sourceCollars, value);
      const match = findCollarByName(targetCollars, name);
      if (match) {
        methods.setValue('collar', String(match.id), { shouldDirty: true });
      }
      continue;
    }
    methods.setValue(key, value, { shouldDirty: true });
  }
}

/**
 * Copies non-empty stole values the item's vestment has onto the form.
 * Panel, cord, and tassel always. Patching only when that vestment has it.
 * A cut only when that vestment still lists it. Length stays untouched.
 */
export function applyStoleDesignPreset(
  methods: UseFormReturn<StoleItemFormValues>,
  values: DesignPreset | PastCombination,
  vestment: Vestment | undefined
): void {
  const setId = (
    name:
      | 'panelFabric'
      | 'panelColor'
      | 'cord'
      | 'tassel'
      | 'patchingFabric'
      | 'patchingColor',
    value: { id: number } | null | undefined
  ) => {
    const id = idToForm(value);
    if (!id) {
      return false;
    }
    methods.setValue(name, id, { shouldDirty: true });
    return true;
  };

  setId('panelFabric', values.panelFabric);
  setId('panelColor', values.panelColor);

  const cutId = values.stoleCut?.id;
  if (cutId != null && vestment?.stoleCuts.some((cut) => cut.id === cutId)) {
    methods.setValue('stoleCut', String(cutId), { shouldDirty: true });
  }
  if (setId('cord', values.cord)) {
    methods.setValue('withCord', true, { shouldDirty: true });
  }
  if (setId('tassel', values.tassel)) {
    methods.setValue('withTassel', true, { shouldDirty: true });
  }
  const features = getStoleFeatures(vestment?.type);
  if (!features?.patching) {
    return;
  }
  const fabricSet = setId('patchingFabric', values.patchingFabric);
  const colorSet = setId('patchingColor', values.patchingColor);
  if (fabricSet || colorSet) {
    methods.setValue('withPatching', true, { shouldDirty: true });
  }
}

export type DesignPresetSummaryContext = {
  fabrics: Fabric[];
  colors: Color[];
  threads: MaterialColor[];
  trimmings: MaterialColor[];
  galoons: MaterialColor[];
  collars: CollarOption[];
  cords: MaterialColor[];
  tassels: MaterialColor[];
  stoleCuts: StoleCut[];
};

/** Materials + design-type collars and cuts for `getDesignPresetSummary`. */
export function getDesignPresetSummaryContext(
  designType: VestmentType | null | undefined,
  materials: {
    vestments: Vestment[];
    fabrics: Fabric[];
    colors: Color[];
    threads: MaterialColor[];
    trimmings: MaterialColor[];
    galoons: MaterialColor[];
    cords?: MaterialColor[];
    tassels?: MaterialColor[];
  }
): DesignPresetSummaryContext {
  const vestment = getDesignPresetCollarVestment(
    materials.vestments,
    designType
  );
  return {
    fabrics: materials.fabrics,
    colors: materials.colors,
    threads: materials.threads,
    trimmings: materials.trimmings,
    galoons: materials.galoons,
    collars: vestment?.collars ?? [],
    cords: materials.cords ?? [],
    tassels: materials.tassels ?? [],
    stoleCuts: vestment?.stoleCuts ?? [],
  };
}

type SummaryFeatures = ChasubleFeatures | PresetFeatures;

export function getDesignPresetSummary(
  values: DesignPreset | PastCombination,
  features: SummaryFeatures,
  ctx: DesignPresetSummaryContext
): OrderItemSummarySegment[];
export function getDesignPresetSummary(
  values: DesignPresetFormValues,
  features: SummaryFeatures,
  ctx: DesignPresetSummaryContext
): OrderItemSummarySegment[];
export function getDesignPresetSummary(
  values: DesignPreset | PastCombination | DesignPresetFormValues,
  features: SummaryFeatures,
  ctx: DesignPresetSummaryContext
): OrderItemSummarySegment[] {
  // Form values use string ids; API shapes use `{ id }` / null.
  const formValues: DesignPresetFormValues =
    typeof (values as DesignPresetFormValues).bodyFabric === 'string'
      ? (values as DesignPresetFormValues)
      : designPresetToFormValues(
          values as (DesignPreset | PastCombination) & { name?: string }
        );
  if ('kind' in features && features.kind === 'stole') {
    return stolePresetSummary(formValues, features.features, ctx);
  }
  const chasubleFeatures: ChasubleFeatures =
    'kind' in features ? features.features : features;
  return chasublePresetSummary(formValues, chasubleFeatures, ctx);
}

function stolePresetSummary(
  formValues: DesignPresetFormValues,
  features: StoleFeatures,
  ctx: DesignPresetSummaryContext
): OrderItemSummarySegment[] {
  return segments(
    fabricSegment(
      'Panel',
      ctx.fabrics,
      ctx.colors,
      formValues.panelFabric,
      formValues.panelColor
    ),
    features.hasCuts
      ? segment('Type', getLabelFromId(ctx.stoleCuts, formValues.stoleCut))
      : null,
    formValues.withCord
      ? segment('Cord', getMaterialSummary(ctx.cords, formValues.cord))
      : null,
    formValues.withTassel
      ? segment('Tassel', getMaterialSummary(ctx.tassels, formValues.tassel))
      : null,
    features.patching && formValues.withPatching
      ? fabricSegment(
          'Patching',
          ctx.fabrics,
          ctx.colors,
          formValues.patchingFabric,
          formValues.patchingColor
        )
      : null
  );
}

function chasublePresetSummary(
  formValues: DesignPresetFormValues,
  features: ChasubleFeatures,
  ctx: DesignPresetSummaryContext
): OrderItemSummarySegment[] {
  const panelFields = features.panel
    ? getChasublePanelFields(features, formValues)
    : [];
  const materialField = getChasubleMaterialField(features, formValues);
  const materials = {
    thread: ctx.threads,
    trimming: ctx.trimmings,
    galoon: ctx.galoons,
  };
  const material = materialField
    ? getMaterialSummary(materials[materialField], formValues[materialField])
    : null;
  return segments(
    fabricSegment(
      'Body',
      ctx.fabrics,
      ctx.colors,
      formValues.bodyFabric,
      formValues.bodyColor
    ),
    panelFields.length
      ? withSegmentColor(
          segment(
            'Panel',
            panelFields.includes('gothicType')
              ? getGothicTypeLabel(formValues.gothicType)
              : null,
            materialField === 'galoon' ? material : null,
            panelFields.includes('fabric')
              ? getLabelFromId(ctx.fabrics, formValues.panelFabric)
              : null,
            panelFields.includes('color')
              ? getLabelFromId(ctx.colors, formValues.panelColor)
              : null,
            panelFields.includes('dimension')
              ? formValues.panelDimension
              : null,
            panelFields.includes('stoleSize') && formValues.stoleSize
              ? `Stole ${formValues.stoleSize}`
              : null
          ),
          ctx.colors,
          formValues.panelColor
        )
      : null,
    panelFields.includes('patching')
      ? segment(
          'Patching',
          getPatchingTypeLabel(formValues.patchingType),
          material
        )
      : null,
    panelFields.includes('trimming') ? segment('Trimming', material) : null,
    panelFields.includes('liningFabric')
      ? fabricSegment(
          'Lining',
          ctx.fabrics,
          ctx.colors,
          formValues.liningFabric,
          formValues.liningColor
        )
      : null,
    features.collar
      ? withSegmentColor(
          segment(
            'Collar',
            getLabelFromId(ctx.collars, formValues.collar),
            getLabelFromId(ctx.fabrics, formValues.collarFabric),
            getLabelFromId(ctx.colors, formValues.collarColor)
          ),
          ctx.colors,
          formValues.collarColor
        )
      : null
  );
}
